import React, { useEffect, useMemo, useState, useContext, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Breadcrumb, Title } from "../../Components/Atoms";

import PATHS from "../../Routes/paths";
import { getDriverOrVehicleStats } from "../../APIs";
import { Button, useToast } from "@chakra-ui/react";

import "./styles.css";
import { getISODate, getLocalisedDate } from "../../Library/datetime";
import { ArrowRightIcon } from "../../Assets/images/svgs";
import { getRidingStatus, getStatLabel } from "../../Library/helpers";
import { DATE_RANGES, RIDING_SCORE_COLORS } from "../../Library/constants";
import { DatePicker } from "../../Components/Molecules";
import { StoreContext } from "../../Library/contexts";
import Loader from "../Loader";

const DAILY_STAT_TYPES = [
  {  title: "Total Distance",  format: "distance",   key: "dst"          },
  {  title: "Average Speed",   format: "speed"   ,   key: "avs"          },
  {  title: "Top Speed",       format: "speed"   ,   key: "tps"          },
  {  title: "Running Time",    format: "duration",   key: "runningTime"  },
  {  title: "Total Duration",  format: "duration",   key: "dur"          },
];

const PastRides = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { vId } = useParams();
  const searchParams = new URLSearchParams(window.location.search);
  const reg = searchParams.get("reg");
  const { filters } = useContext(StoreContext);
  const [showLoading, setShowLoading] = useState(true);
  const [ dailyRides, setDailyRides ] = useState([]);
  const prevDateRange = useRef(DATE_RANGES[filters.range]);

  const isUser = useRef(false);
  const toast = useToast();

  const paths = useMemo(() => [
    { idx: 1, name: "Lease", link: PATHS.LEASE },
    { idx: 2, name: "Vehicles", link: PATHS.LEASED_VEHICLES },
    { idx: 3, name: reg || vId, link: [PATHS.LEASED_VEHICLES, PATHS.VEHICLE_STATS].join("/") + `?idx=${vId}` },
    { idx: 3, name: "Past Rides", isCurrentPage: true },
  ], [ reg, vId ]);

  const userPaths = useMemo(() => {
    let paths = location.pathname.split("/");
    let vehicleId = paths[paths.length - 1];
    return [
      { idx: 1, name: "Live Tracking", link: PATHS.LIVE_TRACKING },
      { idx: 2, name: reg || vId , link: `${PATHS.LIVE_TRACKING}/${PATHS.VEHICLE_STATS}?idx=${vehicleId}&lt=true` },
      { idx: 3, name: "Past Rides", isCurrentPage: true },
    ]
  }, [location.pathname, reg, vId]);

  const fetchDailyStats = () => {
    getDriverOrVehicleStats(vId, null, DATE_RANGES[filters.range], (error, response) => {
      if (error) {
        console.error(error);
        toast({ isClosable: true, description: error, position: "bottom", variant: "solid", status: "error" });
      } else {
        const rides = response.dayWiseStats.map((ride) => {
          return ({
            cards: DAILY_STAT_TYPES.map((stat, i) => ({
              idx: i,
              title: stat.title,
              label: getStatLabel(ride[stat.key] || 0, stat.format),
            })),
            meta: ride,
            score: ride?.rideScore,
            status: getRidingStatus(ride?.rideScore),
          })
        });

        setDailyRides(rides);
      }
      setShowLoading(false);
    });
  };

  useEffect(() => {
    fetchDailyStats();

    let user = window.localStorage.getItem("user");
    try {
      user = JSON.parse(user);
    } catch (e) {
      console.error(e);
    }
    isUser.current = user;
  }, []);

  useEffect(() => {
    if (prevDateRange.current !== DATE_RANGES[filters.range]) {
      fetchDailyStats();
      prevDateRange.current = DATE_RANGES[filters.range];
    }
  }, [filters.range]);

  const handleRide = (ride) => {
    let user = window.localStorage.getItem("user");
    try {
      user = JSON.parse(user);
    } catch (e) {
      console.error(e);
    }

    if (user) {
      navigate([PATHS.LIVE_TRACKING, PATHS.PAST_RIDES, PATHS.RIDE_DETAILS].join("/") + `?reg=${reg}&ts=${ride.ts}` , { state: { ride } });
    } else {
      navigate([PATHS.LEASED_VEHICLES, PATHS.PAST_RIDES, PATHS.RIDE_DETAILS].join("/") + `?reg=${reg}&ts=${ride.ts}`, { state: { ride } });
    }
  };

  return showLoading ? <Loader hide={true} /> :
    <div className="panel-main past-rides">
      <header className="past-rides-header">
        <div className="bread-container">
         <Breadcrumb paths={isUser.current ? userPaths : paths} />
        </div>
        <DatePicker customMaxDate={getISODate()} onClose={() => fetchDailyStats()} />
      </header>

      {
        dailyRides.length > 0
        ?
        dailyRides.map((ride) => (
          <article key={ride.meta.ts} className="card widget-group">
            <header className="widget-header">
              <Title variant="md">{getLocalisedDate(ride.meta.ts, true)}</Title>
            </header>

            <div className="widget-group keyvals">
              {ride.cards.map((stat) => (
                <main className="widget-main" key={stat.idx}>
                  <header>{stat.title}</header>
                  <article>{stat.label}</article>
                </main>
              ))}
            </div>

            <div className="widget-link">
              <Button variant={"ghost"} rightIcon={<ArrowRightIcon />} onClick={() => handleRide(ride.meta)}>
                View Ride
              </Button>
            </div>

            {ride && ride.score ? <div className="riding-score">
              <span className="riding-text">Riding Score</span>
              <div className="score-container" style={{ backgroundColor: RIDING_SCORE_COLORS[ride?.status]}}>
                <span className="score">{Number(ride.score) ? Number(ride.score).toFixed() : ride.score}%</span>
              </div>
            </div> : null}
          </article>
        ))
        :
        <div className="no-rides">
          <span>
            No Rides Found for Selected Date Range !!
          </span>
        </div>
      }

    </div>
};


export default PastRides;